import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { AppType, CloudAppsRedirectUrl, Service, ServiceStatus } from "@/constants/cloud-apps";
import type { ConnectServiceRequestParams } from "@/_helpers/api.types";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { CloudSecurityContext } from "@/constants/general.ts";
import { StoreModule } from "@/constants/store.ts";

export interface CloudAppPermission {
  scope: string;
  granted: boolean;
  requiredInContext: CloudSecurityContext[];
}

export interface CloudAppService {
  name: Service;
  serviceStatus: ServiceStatus;
  connectedThirdPartyApps: number;
  activeProtectedUsers: number;
  inactiveProtectedUsers: number;
  permissions: CloudAppPermission[];
  securityAwarenessAllowed: boolean;
  auditLogEnabled?: boolean;
}

export interface CloudAppsState {
  services: CloudAppService[];
  isLoading: boolean;
  showSkeletonLoader: boolean;
  boxConfigFileError: boolean;
  googleClientId: string;
}

const defaultCloudAppsState = {
  services: [],
  isLoading: false,
  showSkeletonLoader: false,
  boxConfigFileError: false,
  googleClientId: "",
};

export const GOOGLE_PERMISSIONS_SCOPES = `https://www.googleapis.com/auth/userinfo.email,
        https://www.googleapis.com/auth/admin.directory.customer,
        https://mail.google.com/,
        https://www.googleapis.com/auth/admin.directory.domain,
        https://www.googleapis.com/auth/admin.directory.user,
        https://www.googleapis.com/auth/admin.directory.group,
        https://www.googleapis.com/auth/admin.reports.audit.readonly,
        https://www.googleapis.com/auth/gmail.settings.basic,
        https://www.googleapis.com/auth/drive,
        https://www.googleapis.com/auth/gmail.settings.sharing,
        https://www.googleapis.com/auth/admin.directory.user.security`;

export const useCloudAppsStore = defineStore(StoreModule.CLOUD_APPS, {
  state: (): CloudAppsState => ({ ...defaultCloudAppsState }),
  getters: {
    connectedServices(state): CloudAppService[] {
      return state.services.filter((service) =>
        [ServiceStatus.CONNECTED, ServiceStatus.CONNECTED_NOT_SECURE].includes(
          service.serviceStatus
        )
      );
    },
    notConnectedServices(state) {
      return state.services.filter(
        (service) =>
          ![ServiceStatus.CONNECTED, ServiceStatus.CONNECTED_NOT_SECURE].includes(
            service.serviceStatus
          )
      );
    },
  },
  actions: {
    resetGoogleClientId() {
      this.googleClientId = "";
    },
    async getServices(showSkeletonLoader = true) {
      this.showSkeletonLoader = showSkeletonLoader;

      const request = {
        ...api.services,
      };

      try {
        const { data } = await axiosInstance.request(request);

        this.services = data;
      } catch (error) {
        console.error(error);
      }
      this.showSkeletonLoader = false;
    },
    async getLastHeartbeat(service: Service) {
      const request = {
        ...api.cloudAppServiceHeartbeat(),
        params: {
          service,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);

        return data.lastHeartbeat;
      } catch (error) {
        console.error(error);

        return 0;
      }
    },
    async uploadBoxConfiguration(file: File) {
      this.isLoading = true;
      this.boxConfigFileError = false;

      const formData = new FormData();
      formData.append("file", file);

      const request = {
        ...api.uploadBoxConfiguration(formData),
      };

      try {
        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
        this.boxConfigFileError = true;
      }

      this.isLoading = false;
    },
    async connect({
      appType,
      service,
      onboarding,
      languageCode,
      redirectLocation,
      grantPermissions,
    }: ConnectServiceRequestParams) {
      this.isLoading = true;
      const { brandingAlias, workspace } = useWorkspaceStore();

      const request = {
        ...api.connectService(),
        params: {
          brandingAlias,
          service,
          appType: appType || AppType.DEFAULT,
          onboarding: onboarding || false,
          languageCode,
          grantPermissions,
          workspaceId: workspace.workspaceId,
          redirectLocation: redirectLocation || CloudAppsRedirectUrl.CLOUD_APPS_SETTINGS,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);

        window.location.href = data.redirectUrl;
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    },
    async grantPermissions({ appType, service }: ConnectServiceRequestParams) {
      this.isLoading = true;
      const { brandingAlias } = useWorkspaceStore();
      const request = {
        ...api.grantPermissionsForService(),
        params: {
          brandingAlias,
          service,
          appType: appType || AppType.DEFAULT,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);

        window.location.href = data.redirectUrl;
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    },
    async disconnect(payload: Service) {
      this.isLoading = true;

      const request = {
        ...api.disconnectService(payload),
      };

      try {
        await axiosInstance.request(request);

        await this.getServices();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.cloudApps.disconnected", {
            service: i18n.global.t(`services.${payload}`),
          })
        );
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    },
    async completeServiceConnection(service: Service) {
      const request = {
        ...api.completeServiceConnection(),
        params: {
          service,
        },
      };

      try {
        await axiosInstance.request(request);
        await this.getServices();
      } catch (error) {
        console.error(error);
      }
    },
    async fetchGoogleClientId() {
      const request = {
        ...api.fetchGoogleClientId(),
      };

      try {
        const response = await axiosInstance.request(request);

        this.googleClientId = response.data.clientId;
      } catch (error) {
        console.error(error);
      }
    },
    async completePermissionsGranting(service: Service) {
      const request = {
        ...api.completeServiceConnection(),
        params: {
          service,
        },
      };

      try {
        await axiosInstance.request(request);
        await this.getServices();
      } catch (error) {
        console.error(error);
      }
    },
  },
});
